import { ReactNode, useState } from "react";
import { StatusLine } from "./StatusLine";
import { useContacts, useDebouncedValue } from "../hooks/useContacts";
import type { Contact } from "../lib/types";

const PAGE_SIZE = 15;

/** Search-box + paged result list over ``processed_contacts``.
 *
 * Renders the chrome (search input, loading/error/empty states, pagination
 * hint, per-row info block) and delegates the right-side action and any
 * extra below-row content to the caller via render slots. Used by both the
 * single-pick sample manager and the multi-select authoring-start picker.
 */
export function ContactSearchList({
  itemAction,
  itemExtra,
  itemOpacity,
  placeholder,
  header,
}: {
  itemAction: (contact: Contact) => ReactNode;
  itemExtra?: (contact: Contact) => ReactNode;
  itemOpacity?: (contact: Contact) => number;
  placeholder?: string;
  header?: ReactNode;
}) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250);

  const query = useContacts(
    {
      page: 1,
      pageSize: PAGE_SIZE,
      sort: "newest",
      search: debouncedSearch,
      icpName: null,
      status: "all",
      scoreMin: null,
      scoreMax: null,
    },
    true,
  );

  const results = query.data?.contacts ?? [];

  return (
    <div
      style={{
        padding: 12,
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 4,
      }}
    >
      {header}
      <input
        type="text"
        placeholder={placeholder ?? "Search company, domain, email…"}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        style={{ marginBottom: 8, width: "100%" }}
      />
      {query.isLoading && <p className="card-desc">Loading…</p>}
      {query.isError && (
        <StatusLine
          variant="error"
          message={`Search failed: ${query.error?.message ?? "unknown"}`}
        />
      )}
      {!query.isLoading && results.length === 0 && (
        <p className="card-desc">
          {debouncedSearch
            ? `No contacts match "${debouncedSearch}".`
            : "No processed contacts yet — run the lead pipeline first."}
        </p>
      )}
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {results.map((contact) => {
          const extra = itemExtra?.(contact);
          return (
            <li
              key={contact.id}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: "6px 8px",
                marginBottom: 4,
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 4,
                fontSize: 13,
                opacity: itemOpacity?.(contact) ?? 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {contact.company || contact.domain}
                  </div>
                  <div className="card-desc" style={{ fontSize: 11 }}>
                    <code>{contact.domain}</code>
                    {contact.name ? ` · ${contact.name}` : ""}
                    {contact.country ? ` · ${contact.country}` : ""}
                  </div>
                </div>
                {itemAction(contact)}
              </div>
              {extra}
            </li>
          );
        })}
      </ul>
      {query.data && query.data.total > results.length && (
        <p className="card-desc" style={{ marginTop: 4 }}>
          Showing first {results.length} of {query.data.total} — refine the
          search to narrow down.
        </p>
      )}
    </div>
  );
}
